import React from 'react';
import { EmptyState, Skeleton } from '@onward-delivery/design';
import DetailAccordion from '../../components/DetailAccordion';
import ProfileSection from '../../components/ProfileSection';
import { useCarrierTariffsForShipper } from './hooks';
import TariffDetails from './TariffDetails';
import { getBillingInfo } from './utils';

const BillingInfo = ({ shipper }) => {
    const { name, address, email } = getBillingInfo(shipper);
    return (
        <div className="flex min-w-0 flex-col gap-0.5 break-words text-sm text-foreground">
            <span>{name}</span>
            <span className="font-semibold">{address || 'No billing address'}</span>
            {email ? (
                <a href={`mailto:${email}`} className="break-all font-semibold text-primary underline">
                    {email}
                </a>
            ) : (
                <span className="text-muted">No billing email</span>
            )}
        </div>
    );
};

const Tariffs = ({ shipperId }) => {
    const { orderTariffs, driverTariffs, loading, error } = useCarrierTariffsForShipper(shipperId);

    if (loading) return <Skeleton className="mt-6 h-24 w-full" />;
    if (error) {
        return <EmptyState className="py-12" title="Couldn't load tariffs" description="Please try again later." />;
    }

    return (
        <>
            <ProfileSection title="Order Tariffs">
                <DetailAccordion
                    emptyText="No order tariff assigned to this shipper."
                    items={orderTariffs.map((tariff) => ({
                        key: tariff.key,
                        title: tariff.name,
                        content: (
                            <TariffDetails
                                rates={tariff.rates}
                                rows={[
                                    { label: 'Leg', value: tariff.leg || 'Not set' },
                                    {
                                        label: 'Fuel Surcharge',
                                        value: tariff.fuelSurcharge ? 'Added' : 'Not added',
                                        tone: tariff.fuelSurcharge ? 'success' : undefined,
                                    },
                                ]}
                            />
                        ),
                    }))}
                />
            </ProfileSection>
            <ProfileSection title="Driver Tariffs">
                <DetailAccordion
                    emptyText="No driver tariffs."
                    items={driverTariffs.map((tariff) => ({
                        key: tariff.key,
                        title: tariff.name,
                        content: (
                            <TariffDetails
                                rates={tariff.rates}
                                rows={[{ label: 'Applies To', value: tariff.appliesTo }]}
                            />
                        ),
                    }))}
                />
            </ProfileSection>
        </>
    );
};

/**
 * The Billing / Tariffs tab of a shipper's profile, read only.
 * @param {{ partnership: Object }} props
 */
const BillingPage = ({ partnership }) => (
    <>
        <ProfileSection title="Billing Info">
            <BillingInfo shipper={partnership.shipper} />
        </ProfileSection>
        <Tariffs shipperId={partnership.shipper_id} />
    </>
);

export default BillingPage;
